import React, { useCallback, useRef, useState } from "react";
import {
  Badge,
  Box,
  Button,
  Flex,
  HStack,
  Icon,
  IconButton,
  Input,
  List,
  ListIcon,
  ListItem,
  Divider,
  Text,
  VStack,
  useDisclosure,
} from "@chakra-ui/react";
import { AppIcons } from "constants/icons";
import { formatBytes } from "utils/helperFuncUtils";
import ConfirmationModal from "common/modals/ConfirmationModal";

const ACCEPTED_TYPES = ".docx,.dotx";
const MAX_FILE_BYTES = 20 * 1024 * 1024;
const UPLOAD_ZONE_MIN_H = "220px";

interface ReportTemplateTabProps {
  onUpload: (file: File) => Promise<void>;
  isUploading: boolean;
  onDownload: () => Promise<void>;
  onDelete: () => Promise<void>;
  isDeleting: boolean;
}

const isAcceptedTemplateFile = (file: File): boolean => {
  const name = file.name.toLowerCase();
  return name.endsWith(".docx") || name.endsWith(".dotx");
};

const ReportTemplateTab: React.FC<ReportTemplateTabProps> = ({
  onUpload,
  isUploading,
  onDownload,
  onDelete,
  isDeleting,
}) => {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const [fileError, setFileError] = useState<string | null>(null);
  const { isOpen: isDeleteOpen, onOpen: onDeleteOpen, onClose: onDeleteClose } = useDisclosure();

  const stageFile = useCallback((file: File | undefined) => {
    if (!file) return;
    if (!isAcceptedTemplateFile(file)) {
      setFileError("Only .docx or .dotx files are supported.");
      return;
    }
    if (file.size > MAX_FILE_BYTES) {
      setFileError("File must be 20 MB or smaller.");
      return;
    }
    setFileError(null);
    setSelectedFile(file);
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    stageFile(e.target.files?.[0]);
  };

  const handleClear = () => {
    setSelectedFile(null);
    setFileError(null);
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleUpload = async () => {
    if (!selectedFile) return;
    await onUpload(selectedFile)
      .then(handleClear)
      .catch(() => undefined);
  };

  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    if (e.currentTarget.contains(e.relatedTarget as Node)) return;
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
    stageFile(e.dataTransfer.files?.[0]);
  };

  const openFilePicker = () => fileInputRef.current?.click();

  const handleConfirmDelete = async () => {
    await onDelete().catch(() => undefined);
    onDeleteClose();
  };

  return (
    <Box px={{ base: 4, md: 6 }} py={2} maxW="640px" w="full">
      <Box borderWidth="1px" borderColor="gray.200" borderRadius="xl" bg="white" p={6} shadow="sm">
        <Text fontSize="sm" color="fg.muted" mb={5}>
          Upload a Word template to use for all new reports. This replaces the current template immediately.
        </Text>

        {selectedFile ? (
          <VStack
            minH={UPLOAD_ZONE_MIN_H}
            spacing={4}
            align="stretch"
            justify="center"
            px={5}
            py={5}
            borderWidth="1px"
            borderColor="purple.200"
            borderRadius="lg"
            bg="purple.50"
          >
            <HStack spacing={3} align="start">
              <Flex
                align="center"
                justify="center"
                boxSize={11}
                borderRadius="md"
                bg="white"
                borderWidth="1px"
                borderColor="purple.100"
                flexShrink={0}
              >
                <Icon as={AppIcons.document} boxSize={5} color="purple.600" />
              </Flex>
              <Box flex="1" minW={0}>
                <Text fontSize="sm" fontWeight="600" color="fg.default" noOfLines={2}>
                  {selectedFile.name}
                </Text>
                <Text fontSize="xs" color="fg.muted" mt={1}>
                  {formatBytes(selectedFile.size)}
                </Text>
              </Box>
              <IconButton
                aria-label="Remove file"
                icon={<Icon as={AppIcons.close} boxSize={3.5} />}
                size="sm"
                variant="ghost"
                colorScheme="gray"
                onClick={handleClear}
              />
            </HStack>
            <HStack justify="flex-end" spacing={2} pt={1}>
              <Button variant="ghost" size="sm" onClick={handleClear} color="fg.muted">
                Cancel
              </Button>
              <Button
                colorScheme="purple"
                bg="purple.700"
                color="white"
                _hover={{ bg: "purple.600" }}
                size="sm"
                isLoading={isUploading}
                loadingText="Uploading…"
                onClick={handleUpload}
              >
                Confirm upload
              </Button>
            </HStack>
          </VStack>
        ) : (
          <VStack
            minH={UPLOAD_ZONE_MIN_H}
            spacing={3}
            justify="center"
            px={6}
            py={6}
            borderWidth="2px"
            borderStyle="dashed"
            borderColor={isDragging ? "purple.400" : "gray.300"}
            borderRadius="lg"
            bg={isDragging ? "purple.50" : "gray.50"}
            cursor="pointer"
            onClick={openFilePicker}
            onDragOver={handleDragOver}
            onDragEnter={handleDragEnter}
            onDragLeave={handleDragLeave}
            onDrop={handleDrop}
            _hover={{ bg: "purple.50", borderColor: "purple.300" }}
            transition="background 0.15s, border-color 0.15s"
          >
            <Flex
              align="center"
              justify="center"
              boxSize={12}
              borderRadius="full"
              bg="white"
              borderWidth="1px"
              borderColor="gray.200"
            >
              <Icon as={AppIcons.uploadOutline} boxSize={6} color={isDragging ? "purple.500" : "gray.400"} />
            </Flex>
            <Text fontSize="sm" color="fg.default" textAlign="center" fontWeight="500">
              Drag and drop your file here
            </Text>
            <Text fontSize="sm" color="fg.muted" textAlign="center">
              or{" "}
              <Text as="span" color="purple.600" fontWeight="600">
                browse to choose
              </Text>
            </Text>
            <HStack spacing={2} pt={1}>
              <Badge colorScheme="purple" variant="subtle" fontSize="xs">
                .docx
              </Badge>
              <Badge colorScheme="purple" variant="subtle" fontSize="xs">
                .dotx
              </Badge>
              <Badge colorScheme="gray" variant="subtle" fontSize="xs">
                max 20 MB
              </Badge>
            </HStack>
          </VStack>
        )}

        {fileError && (
          <Text fontSize="xs" color="red.500" mt={2}>
            {fileError}
          </Text>
        )}

        <List spacing={2} mt={5} fontSize="xs" color="fg.muted">
          <ListItem display="flex" alignItems="flex-start">
            <ListIcon as={AppIcons.infoOutline} color="purple.400" mt={0.5} />
            Upload takes effect for all new reports right away.
          </ListItem>
          <ListItem display="flex" alignItems="flex-start">
            <ListIcon as={AppIcons.infoOutline} color="purple.400" mt={0.5} />
            Existing reports keep the template they were generated with.
          </ListItem>
        </List>
      </Box>

      <Divider my={4} borderColor="gray.200" />

      <HStack justify="space-between" align="center" spacing={4}>
        <Text fontSize="xs" color="fg.muted" mb={0}>
          Need the file currently on the server?
        </Text>
        <HStack spacing={3}>
          <Button
            variant="link"
            size="sm"
            color="purple.600"
            fontWeight="500"
            flexShrink={0}
            leftIcon={<Icon as={AppIcons.download} boxSize={3.5} />}
            onClick={onDownload}
          >
            Download current
          </Button>
          <Button
            variant="link"
            size="sm"
            color="red.500"
            fontWeight="500"
            flexShrink={0}
            leftIcon={<Icon as={AppIcons.trashLine} boxSize={3.5} />}
            onClick={onDeleteOpen}
          >
            Remove current
          </Button>
        </HStack>
      </HStack>

      <ConfirmationModal
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        onConfirm={handleConfirmDelete}
        title="Remove template?"
        text="Delete the active template? New reports will use the default plain style until a replacement is uploaded."
        confirmText="Remove"
        confirmColorScheme="red"
        isLoading={isDeleting}
      />

      <Input ref={fileInputRef} type="file" accept={ACCEPTED_TYPES} display="none" onChange={handleFileChange} />
    </Box>
  );
};

export default ReportTemplateTab;
